import {
  DeleteOutlined,
  InboxOutlined,
  PaperClipOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Card,
  Col,
  ColorPicker,
  DatePicker,
  message,
  Row,
  Space,
  Typography,
  Upload,
} from 'antd';
import { RcFile } from 'antd/es/upload';
import dayjs from 'dayjs';
import React, { useEffect, useMemo } from 'react';
import {
  Controller, FormProvider, useForm, useWatch
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { DropdownElement } from 'common/components/DropdownType';
import HeaderPage from 'common/components/HeaderPage';
import Input from 'common/components/Input';
import ManagementInfo from 'common/components/ManagementInfo';
import SelectFile from 'common/components/SelectFile';
import useChunkUpload from 'common/hooks/useUploadChunks';
import {
  createClientService,
  getClientByIdService,
  mergeChunkService,
  sendMailToClientService,
  updateClientService,
  uploadChunkService,
} from 'common/services/extends/client';
import { CreateClientParams } from 'common/services/extends/client/types';
import { ROUTE_PATHS } from 'common/utils/constant';
import { clientSchema } from 'common/utils/schemas';
import roles from 'configs/roles';

const { RangePicker } = DatePicker;

type ClientFormData = {
  companyName: string;
  taxCode: string;
  logo: string;
  brandColor: string;
  corporateName: string;
  corporateEmail: string;
  corporatePhone: string;
  technicalName: string;
  technicalEmail: string;
  technicalPhone: string;
  contractNumber: string;
  dateRange: string[];
  contractType: OptionType;
  contractFiles: RcFile[];
};

const ClientDetail: React.FC<ActiveRoles> = ({ roleOther }) => {
  /* Hooks */
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { uploadChunks, uploading } = useChunkUpload({
    uploadChunkService,
    mergeChunkService,
  });
  const [searchParams] = useSearchParams();
  const idParams = Number(searchParams.get('id'));
  const method = useForm<ClientFormData>({
    defaultValues: {},
    resolver: yupResolver(clientSchema),
  });

  /* States */
  const [userFiles, setUserFiles] = React.useState<IMergeChunkResponse[]>([]);

  /* Queries */
  const { data, isLoading } = useQuery(
    ['getClientDetail', { idParams }],
    () => {
      if (idParams) {
        return getClientByIdService(idParams);
      }
      return undefined;
    }
  );

  const { mutate: createMutate, isLoading: createLoading } = useMutation(
    ['createClient'],
    async (params: CreateClientParams) => createClientService(params),
    {
      onSuccess: () => {
        message.success(t('message.createSuccess'));
        navigate(`${ROUTE_PATHS.CLIENT_MANAGEMENT}`);
      },
      onError: () => {
        message.error(t('message.createError'));
      },
    }
  );

  const { mutate: updateMutate, isLoading: updateLoading } = useMutation(
    ['updateClient'],
    async (params: CreateClientParams) => updateClientService(idParams, params),
    {
      onSuccess: () => {
        message.success(t('message.updateSuccess'));
        queryClient.invalidateQueries(['getClientDetail', { idParams }]);
      },
      onError: () => {
        message.error(t('message.updateError'));
      },
    }
  );

  const { mutate: sendMailMutate, isLoading: sendMailLoading } = useMutation(
    ['SendMailClient'],
    sendMailToClientService,
    {
      onSuccess: () => {
        message.success(t('client.sendMailSuccess'));
      },
      onError: () => {
        message.error(t('client.sendMailError'));
      },
    }
  );

  /* Variables */
  const contractTypeOptions = useMemo(
    () => [
      { label: t('client.official'), value: 1 },
      { label: t('client.poc'), value: 2 },
      { label: t('client.trial'), value: 3 },
      { label: t('client.expired'), value: 4 },
    ],
    [t]
  );

  const fileList = useWatch({
    control: method.control,
    name: 'contractFiles',
  });

  /* Functions */
  const onSubmit = async () => {
    let filePaths: IMergeChunkResponse[] | undefined;
    const {
      contractFiles,
      companyName,
      taxCode,
      logo,
      brandColor,
      corporateName,
      corporateEmail,
      corporatePhone,
      technicalName,
      technicalEmail,
      technicalPhone,
      contractNumber,
      dateRange,
      contractType,
    } = method.getValues();
    if (
      !contractFiles
      || (contractFiles.length === 0 && userFiles.length === 0)
    ) {
      method.setError('contractFiles', {
        message: 'Vui lòng tải lên ít nhất một tệp hợp đồng.',
      });
      return;
    }
    const uploadPromises = contractFiles.map((file) => uploadChunks(file));
    try {
      const uploadResults = await Promise.all(uploadPromises);
      filePaths = uploadResults.filter((result) => result !== null) as IMergeChunkResponse[];
    } catch (error) {
      method.setError('contractFiles', {
        message: 'Lỗi khi tải lên tệp. Vui lòng thử lại sau.',
      });
      return;
    }
    if (idParams) {
      updateMutate({
        companyName,
        taxCode,
        logo,
        brandColor,
        corporateName,
        corporateEmail,
        corporatePhone,
        technicalName,
        technicalEmail,
        technicalPhone,
        contractNumber,
        startDate: dayjs(dateRange[0]).format('YYYY-MM-DD'),
        endDate: dayjs(dateRange[1]).format('YYYY-MM-DD'),
        contractType: contractType.value as number,
        contractFiles: [...userFiles, ...filePaths],
      });
    } else {
      createMutate({
        companyName,
        taxCode,
        logo,
        brandColor,
        corporateName,
        corporateEmail,
        corporatePhone,
        technicalName,
        technicalEmail,
        technicalPhone,
        contractNumber,
        startDate: dayjs(dateRange[0]).format('YYYY-MM-DD'),
        endDate: dayjs(dateRange[1]).format('YYYY-MM-DD'),
        contractType: contractType.value as number,
        contractFiles: [...userFiles, ...filePaths],
      });
    }
  };

  const beforeUpload = (file: RcFile) => {
    const isPDF = file.type === 'application/pdf';
    if (!isPDF) {
      message.error('You can only upload PDF file!');
    }
    return isPDF || Upload.LIST_IGNORE;
  };

  /* Effects */
  useEffect(() => {
    if (data) {
      method.reset({
        companyName: data.clientData.companyName,
        taxCode: data.clientData.taxCode,
        logo: data.clientData.logo,
        brandColor: data.clientData.brandColor,
        corporateName: data.clientData.corporateName,
        corporateEmail: data.clientData.corporateEmail,
        corporatePhone: data.clientData.corporatePhone,
        technicalName: data.clientData.technicalName,
        technicalEmail: data.clientData.technicalEmail,
        technicalPhone: data.clientData.technicalPhone,
        contractNumber: data.clientData.contractNumber,
        dateRange: [data.clientData.startDate, data.clientData.endDate],
        contractType: contractTypeOptions.find(
          (option) => option.value === data.clientData.contractType
        ),
        contractFiles: [],
      });
    }
  }, [method, data, contractTypeOptions]);

  useEffect(() => {
    if (data) {
      setUserFiles(data.clientData.contractFiles || []);
    }
  }, [data]);

  /* Render */
  return (
    <FormProvider<ClientFormData> {...method}>
      <HeaderPage
        fixed
        title={idParams ? 'Chỉnh sửa' : 'Tạo mới'}
        rightHeader={(
          <Space>
            {!!idParams && roleOther.includes(roles.CLIENT_SEND_MAIL) && (
              <Button
                onClick={() => sendMailMutate(idParams)}
                loading={sendMailLoading}
                disabled={
                  !roleOther.includes(roles.CLIENT_SEND_MAIL) || sendMailLoading
                }
              >
                {t('client.sendMail')}
              </Button>
            )}
            <Button
              type="primary"
              loading={isLoading || createLoading || updateLoading || uploading}
              onClick={method.handleSubmit(onSubmit)}
            >
              <SaveOutlined />
              {t('system.save')}
            </Button>
          </Space>
        )}
      />
      <div className="t-mainlayout_wrapper">
        <Row gutter={16}>
          <Col
            xxl={idParams ? 18 : 24}
            xl={idParams ? 16 : 24}
            lg={idParams ? 16 : 24}
          >
            <Card
              title={(
                <Typography.Title level={5}>
                  {t('client.clientInfo')}
                </Typography.Title>
              )}
            >
              <Row gutter={16}>
                <Col span={12} className="u-mb-12">
                  <Typography.Text strong>
                    {t('client.companyName')}
                    {' '}
                  </Typography.Text>
                  <Typography.Text strong type="danger">
                    *
                  </Typography.Text>
                  <Controller
                    name="companyName"
                    render={({
                      field: { value, onChange },
                      fieldState: { error },
                    }) => (
                      <Input
                        className="u-mt-8"
                        name="companyName"
                        readOnly={!!idParams}
                        placeholder={`${t('system.input')} ${t(
                          'client.companyName'
                        )}`}
                        value={value}
                        onChange={onChange}
                        error={error?.message}
                        size="large"
                      />
                    )}
                  />
                </Col>
                <Col span={12} className="u-mb-12">
                  <Typography.Text strong>
                    {t('client.taxCode')}
                    {' '}
                  </Typography.Text>
                  <Typography.Text strong type="danger">
                    *
                  </Typography.Text>
                  <Controller
                    name="taxCode"
                    render={({
                      field: { value, onChange },
                      fieldState: { error },
                    }) => (
                      <Input
                        className="u-mt-8"
                        name="taxCode"
                        readOnly={!!idParams}
                        placeholder={`${t('system.input')} ${t(
                          'client.taxCode'
                        )}`}
                        value={value}
                        onChange={onChange}
                        error={error?.message}
                        size="large"
                      />
                    )}
                  />
                </Col>
                <Col span={12} className="u-mb-12">
                  <Typography.Text strong>
                    {t('client.logo')}
                    {' '}
                  </Typography.Text>
                  <Controller
                    name="logo"
                    render={({ field: { value, onChange } }) => (
                      <div className="u-mt-8">
                        <SelectFile
                          value={value}
                          name="logo"
                          handleSelect={onChange}
                          handleDelete={() => onChange(undefined)}
                          title=""
                        />
                      </div>
                    )}
                  />
                </Col>
                <Col span={12} className="u-mb-12">
                  <div>
                    <Typography.Text strong>
                      {t('client.startDate')}
                      {' '}
                      /
                      {t('client.endDate')}
                      {' '}
                    </Typography.Text>
                    <Typography.Text strong type="danger">
                      *
                    </Typography.Text>
                    <Controller
                      name="dateRange"
                      render={({
                        field: { value, onChange },
                        fieldState: { error },
                      }) => (
                        <>
                          <RangePicker
                            className="u-mt-8"
                            allowClear
                            size="large"
                            onChange={(val) => onChange(val)}
                            value={
                              value
                                ? [dayjs(value[0]), dayjs(value[1])]
                                : undefined
                            }
                            style={{ width: '100%' }}
                          />
                          {error && (
                            <span className="a-input_errorMessage">
                              {error.message}
                            </span>
                          )}
                        </>
                      )}
                    />
                  </div>
                  <div
                    className="u-mt-12"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Typography.Text strong>
                      {t('client.brandColor')}
                    </Typography.Text>
                    <Controller
                      name="brandColor"
                      render={({ field: { value, onChange } }) => (
                        <ColorPicker
                          disabledAlpha
                          allowClear
                          size="large"
                          className="u-mt-8"
                          value={value}
                          onChange={(color) => onChange(color.toHexString())}
                          onClear={() => onChange(undefined)}
                        />
                      )}
                    />
                  </div>
                </Col>
              </Row>
            </Card>
            <Card
              title={(
                <Typography.Title level={5}>
                  {t('client.corporateAccount')}
                </Typography.Title>
              )}
            >
              <Row gutter={16}>
                <Col span={12} className="u-mb-12">
                  <Typography.Text strong>
                    {t('client.corporateName')}
                    {' '}
                  </Typography.Text>
                  <Typography.Text strong type="danger">
                    *
                  </Typography.Text>
                  <Controller
                    name="corporateName"
                    render={({
                      field: { value, onChange },
                      fieldState: { error },
                    }) => (
                      <Input
                        className="u-mt-8"
                        name="corporateName"
                        placeholder={`${t('system.input')} ${t(
                          'client.corporateName'
                        )}`}
                        value={value}
                        onChange={onChange}
                        error={error?.message}
                        size="large"
                      />
                    )}
                  />
                </Col>
                <Col span={12} className="u-mb-12">
                  <Typography.Text strong>
                    {t('client.corporateEmail')}
                    {' '}
                  </Typography.Text>
                  <Typography.Text strong type="danger">
                    *
                  </Typography.Text>
                  <Controller
                    name="corporateEmail"
                    render={({
                      field: { value, onChange },
                      fieldState: { error },
                    }) => (
                      <Input
                        className="u-mt-8"
                        name="corporateEmail"
                        readOnly={!!idParams}
                        placeholder={`${t('system.input')} ${t(
                          'client.corporateEmail'
                        )}`}
                        value={value}
                        onChange={onChange}
                        error={error?.message}
                        size="large"
                      />
                    )}
                  />
                </Col>
                <Col span={12} className="u-mb-12">
                  <Typography.Text strong>
                    {t('client.corporatePhone')}
                    {' '}
                  </Typography.Text>
                  <Typography.Text strong type="danger">
                    *
                  </Typography.Text>
                  <Controller
                    name="corporatePhone"
                    render={({
                      field: { value, onChange },
                      fieldState: { error },
                    }) => (
                      <Input
                        className="u-mt-8"
                        name="corporatePhone"
                        placeholder={`${t('system.input')} ${t(
                          'client.corporatePhone'
                        )}`}
                        value={value}
                        onChange={onChange}
                        error={error?.message}
                        size="large"
                      />
                    )}
                  />
                </Col>
              </Row>
            </Card>
            <Card
              title={(
                <Typography.Title level={5}>
                  {t('client.technicalAccount')}
                </Typography.Title>
              )}
            >
              <Row gutter={16}>
                <Col span={12} className="u-mb-12">
                  <Typography.Text strong>
                    {t('client.technicalName')}
                    {' '}
                  </Typography.Text>
                  <Typography.Text strong type="danger">
                    *
                  </Typography.Text>
                  <Controller
                    name="technicalName"
                    render={({
                      field: { value, onChange },
                      fieldState: { error },
                    }) => (
                      <Input
                        className="u-mt-8"
                        name="technicalName"
                        placeholder={`${t('system.input')} ${t(
                          'client.technicalName'
                        )}`}
                        value={value}
                        onChange={onChange}
                        error={error?.message}
                        size="large"
                      />
                    )}
                  />
                </Col>
                <Col span={12} className="u-mb-12">
                  <Typography.Text strong>
                    {t('client.technicalEmail')}
                    {' '}
                  </Typography.Text>
                  <Typography.Text strong type="danger">
                    *
                  </Typography.Text>
                  <Controller
                    name="technicalEmail"
                    render={({
                      field: { value, onChange },
                      fieldState: { error },
                    }) => (
                      <Input
                        className="u-mt-8"
                        name="technicalEmail"
                        placeholder={`${t('system.input')} ${t(
                          'client.technicalEmail'
                        )}`}
                        value={value}
                        onChange={onChange}
                        error={error?.message}
                        size="large"
                      />
                    )}
                  />
                </Col>
                <Col span={12} className="u-mb-12">
                  <Typography.Text strong>
                    {t('client.technicalPhone')}
                    {' '}
                  </Typography.Text>
                  <Typography.Text strong type="danger">
                    *
                  </Typography.Text>
                  <Controller
                    name="technicalPhone"
                    render={({
                      field: { value, onChange },
                      fieldState: { error },
                    }) => (
                      <Input
                        className="u-mt-8"
                        name="technicalPhone"
                        placeholder={`${t('system.input')} ${t(
                          'client.technicalPhone'
                        )}`}
                        value={value}
                        onChange={onChange}
                        error={error?.message}
                        size="large"
                      />
                    )}
                  />
                </Col>
              </Row>
            </Card>
            <Card
              title={(
                <Typography.Title level={5}>
                  {t('client.contractInfo')}
                </Typography.Title>
              )}
            >
              <Row gutter={16}>
                <Col span={12} className="u-mb-12">
                  <Typography.Text strong>
                    {t('client.contractType')}
                    {' '}
                  </Typography.Text>
                  <Typography.Text strong type="danger">
                    *
                  </Typography.Text>
                  <Controller
                    name="contractType"
                    render={({
                      field: { value, onChange },
                      fieldState: { error },
                    }) => (
                      <>
                        <DropdownElement
                          options={contractTypeOptions}
                          placeholder={`${t('system.select')} ${t(
                            'client.contractType'
                          )}`}
                          locale="vi"
                          value={value}
                          onChange={onChange}
                          isGetOption
                          size="large"
                        />
                        {error && (
                          <span className="a-input_errorMessage">
                            {(error as any).value.message}
                          </span>
                        )}
                      </>
                    )}
                  />
                </Col>
                <Col span={12} className="u-mb-12">
                  <Typography.Text strong>
                    {t('client.contractNumber')}
                    {' '}
                  </Typography.Text>
                  <Typography.Text strong type="danger">
                    *
                  </Typography.Text>
                  <Controller
                    name="contractNumber"
                    render={({
                      field: { value, onChange },
                      fieldState: { error },
                    }) => (
                      <Input
                        className="u-mt-8"
                        name="contractNumber"
                        placeholder={`${t('system.input')} ${t(
                          'client.contractNumber'
                        )}`}
                        value={value}
                        onChange={onChange}
                        error={error?.message}
                        size="large"
                      />
                    )}
                  />
                </Col>
                <Col span={24} className="u-mb-12">
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <div>
                      <Typography.Text strong>
                        {t('client.contractFiles')}
                        {' '}
                      </Typography.Text>
                      <Typography.Text strong type="danger">
                        *
                      </Typography.Text>
                    </div>
                    <Controller
                      name="contractFiles"
                      render={({
                        field: { onChange },
                        fieldState: { error },
                      }) => (
                        <>
                          <Upload.Dragger
                            multiple
                            onChange={(fileTypes) => onChange(
                              fileTypes.fileList.map(
                                (file) => file.originFileObj as RcFile
                              )
                            )}
                            beforeUpload={beforeUpload}
                            accept="application/pdf"
                            showUploadList={false}
                            className="u-mt-8"
                            customRequest={() => { }}
                            style={{
                              width: '100%',
                            }}
                          >
                            <p className="ant-upload-drag-icon">
                              <InboxOutlined />
                            </p>
                            <p className="ant-upload-text">
                              {t('client.selectFiles')}
                            </p>
                          </Upload.Dragger>
                          {error && (
                            <span className="a-input_errorMessage">
                              {error.message}
                            </span>
                          )}
                        </>
                      )}
                    />

                    {((fileList && fileList.length > 0)
                      || userFiles.length > 0) && (
                        <div className="u-mt-8">
                          <Typography.Title level={5}>
                            {t('client.selectedFiles')}
                          </Typography.Title>
                          {userFiles.map((file) => (
                            <div
                              className="u-mt-8"
                              key={file.path}
                              style={{
                                color: '#1d8daa',
                                cursor: 'pointer',
                                width: 'fit-content',
                              }}
                            >
                              <PaperClipOutlined />
                              {' '}
                              {file.name}
                              <span
                                style={{
                                  color: '#1d8daa',
                                  marginLeft: 8,
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  width: 16,
                                  justifyContent: 'center',
                                  height: 16,
                                  borderRadius: '50%',
                                }}
                                onClick={() => {
                                  setUserFiles((prev) => prev.filter((f) => f !== file));
                                }}
                              >
                                <DeleteOutlined />
                              </span>
                            </div>
                          ))}
                          {fileList.map((file: any, idx) => (
                            <div
                              className="u-mt-8"
                              key={`${file.uid}-${file.name}-${idx.toString()}`}
                              style={{
                                color: '#1d8daa',
                                cursor: 'pointer',
                                width: 'fit-content',
                              }}
                            >
                              <PaperClipOutlined />
                              {' '}
                              {file.name}
                              <span
                                style={{
                                  color: '#1d8daa',
                                  marginLeft: 8,
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  width: 16,
                                  justifyContent: 'center',
                                  height: 16,
                                  borderRadius: '50%',
                                }}
                                onClick={() => {
                                  const newFileList = fileList.filter(
                                    (f: any) => f.uid !== file.uid
                                  );
                                  method.setValue('contractFiles', newFileList);
                                }}
                              >
                                <DeleteOutlined />
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
          {!!idParams && (
            <Col xxl={6} xl={8} lg={8}>
              <ManagementInfo
                createdDate={
                  data ? dayjs(data.clientData.createdAt).fromNow() : ''
                }
                createdBy={data?.creator.name || 'Admin'}
                lastUpdated={
                  data ? dayjs(data.clientData.updatedAt).fromNow() : ''
                }
                lastUpdatedBy={data?.updater.name || 'Admin'}
              />
            </Col>
          )}
        </Row>
      </div>
    </FormProvider>
  );
};

export default ClientDetail;
